import { ESCUDOS_MUNDO } from './escudos-mundo';
import type { Clube } from '../core';
import { cor, esc } from './util';

/** Ícones do menu superior (desenhados do zero, só traços simples). */
const ICONES: Record<string, string> = {
  agenda: '<rect x="3" y="5" width="18" height="16" rx="2"/><path d="M3 10h18M8 3v4M16 3v4"/><path d="M7 14h2M11 14h2M15 14h2M7 17h2M11 17h2"/>',
  tabelas: '<path d="M7 4h10v5a5 5 0 0 1-10 0z"/><path d="M7 6H4v2a3 3 0 0 0 3 3M17 6h3v2a3 3 0 0 1-3 3M12 14v4M8 21h8M10 18h4"/>',
  estadio: '<ellipse cx="12" cy="12" rx="9" ry="6"/><ellipse cx="12" cy="12" rx="4.5" ry="2.5"/><path d="M12 6v12"/>',
  times: '<path d="M8 3 3 6l2 4 2-1v12h10V9l2 1 2-4-5-3a4 4 0 0 1-8 0z"/>',
  procura: '<circle cx="10.5" cy="10.5" r="6.5"/><path d="m16 16 5 5"/>',
  historia: '<circle cx="12" cy="12" r="9"/><path d="M12 7v5l3.5 2"/>',
  opcoes: '<circle cx="12" cy="12" r="3.2"/><path d="M12 2v3M12 19v3M2 12h3M19 12h3M4.9 4.9 7 7M17 17l2.1 2.1M19.1 4.9 17 7M7 17l-2.1 2.1"/>',
  juniores: '<circle cx="12" cy="8" r="3.5"/><path d="M5 21c0-4.5 3-7 7-7s7 2.5 7 7"/><path d="M9 5.5 12 3l3 2.5"/>',
  tecnicos: '<path d="M4 14c0-5 3.5-8 8-8s8 3 8 8z"/><path d="M3 14h18l-1 3H4z"/><path d="M12 6v-2"/>',
  taca: '<path d="M7 4h10v6a5 5 0 0 1-10 0z"/><path d="M7 6H4v2a3 3 0 0 0 3 3M17 6h3v2a3 3 0 0 1-3 3M12 15v4M8 21h8M9.5 19h5"/>',
  leilao: '<path d="M14 4l6 6-3 3-6-6z"/><path d="M11 7 5 13l3 3 6-6"/><path d="M3 21h10M7 17l-3 3"/>',
  bola: '<circle cx="12" cy="12" r="9"/><path d="m12 7 4 3-1.5 4.5h-5L8 10z"/>',
  salvar: '<path d="M4 4h13l3 3v13H4z"/><path d="M8 4v6h8V4M8 20v-6h8v6"/>',
  editor: '<path d="M4 20h4L19 9l-4-4L4 16z"/><path d="m14 6 4 4"/>',
  casa: '<path d="m3 11 9-8 9 8"/><path d="M5 10v10h5v-6h4v6h5V10"/>',
  ranking: '<path d="M4 21V12h5v9M9.5 21V6h5v15M15 21v-6h5v6M3 21h18"/><path d="m12 2 .9 1.9 2 .3-1.5 1.4.4 2L12 6.6 10.2 7.6l.4-2L9.1 4.2l2-.3z"/>',
  loja: '<path d="M4 8h16l-1.2 12H5.2z"/><path d="M8.5 8V6.5a3.5 3.5 0 0 1 7 0V8"/><path d="M9 12.5c.6 1.2 1.7 1.8 3 1.8s2.4-.6 3-1.8"/>',
  moeda: '<circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="6.6"/><path d="M8.4 9.6l1.5 5 2.1-3.8 2.1 3.8 1.5-5"/>',
};

export function icone(nome: string, tamanho = 28): string {
  const d = ICONES[nome] ?? ICONES.bola;
  return `<svg class="ico" width="${tamanho}" height="${tamanho}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${d}</svg>`;
}

/**
 * Escudo do clube: usa public/escudos/<SIGLA>.svg quando o arquivo existe; senão (ou se falhar) mostra o círculo com as duas cores.
 * A imagem é carregada por <img>, que não executa script, mesmo se o SVG tiver algum.
 */
/** Logo do campeonato (public/escudos/COPADOBRASIL.svg ou SERIEA.svg). Se o arquivo faltar, a imagem some sozinha. */
export function logoCampeonato(tipo: 'copa' | 'liga' | 'serie-b' | 'libertadores' | 'sulamericana' | string, altura = 22): string {
  const arq = tipo === 'copa' || tipo === 'copa-do-brasil' ? 'COPADOBRASIL' : tipo === 'serie-b' ? 'SERIEB' : tipo === 'libertadores' ? 'LIBERTADORES' : tipo === 'sulamericana' ? 'SULAMERICANA' : 'SERIEA';
  return `<img class="logo-camp" src="./escudos/${arq}.svg" alt="" style="height:${altura}px" decoding="sync" />`;
}

/** Siglas cujo arquivo não existe: não pedimos de novo a cada redesenho. */
const semEscudo = new Set<string>();

export function marcarSemEscudo(sigla: string): void {
  if (sigla) semEscudo.add(sigla);
}

/** Escudo de clube de liga estrangeira pelo nome (ver escudos-mundo.ts); sem arquivo, só o círculo neutro. */
export function escudoMundo(nome: string, grande = false): string {
  const arq = ESCUDOS_MUNDO[nome];
  const chave = `mundo:${arq ?? nome}`;
  const img = arq && !semEscudo.has(chave) ? `<img class="escudo-img" data-sigla="${esc(chave)}" src="./escudos/${arq}.svg" alt="" loading="lazy" decoding="async" />` : '';
  return `<span class="escudo${grande ? ' grande' : ''}" style="--a:#6b7280;--b:#d1d5db" title="${esc(nome)}" aria-hidden="true">${img}</span>`;
}

export function escudo(c: Pick<Clube, 'cor1' | 'cor2' | 'sigla'> & { nome?: string; liga?: string }, grande = false): string {
  if (c.liga) {
    // clube de liga estrangeira: o arquivo vem do nome (a sigla pode coincidir com a de um clube brasileiro); sem arquivo, só as cores
    const arq = c.nome ? ESCUDOS_MUNDO[c.nome] : undefined;
    const chave = `mundo:${arq ?? c.nome ?? c.sigla}`;
    const img = arq && !semEscudo.has(chave) ? `<img class="escudo-img" data-sigla="${esc(chave)}" src="./escudos/${arq}.svg" alt="" decoding="sync" />` : '';
    return `<span class="escudo${grande ? ' grande' : ''}" style="--a:${cor(c.cor1)};--b:${cor(c.cor2)}" title="${esc(c.sigla)}" aria-hidden="true">${img}</span>`;
  }
  const img = semEscudo.has(c.sigla) ? '' : `<img class="escudo-img" data-sigla="${esc(c.sigla)}" src="./escudos/${encodeURIComponent(c.sigla)}.svg" alt="" decoding="sync" />`;
  return `<span class="escudo${grande ? ' grande' : ''}" style="--a:${cor(c.cor1)};--b:${cor(c.cor2)}" title="${esc(c.sigla)}" aria-hidden="true">${img}</span>`;
}

const semRosto = new Set<string>();

export function marcarSemRosto(arquivo: string): void {
  if (arquivo) semRosto.add(arquivo);
}

/** "Kaio Jorge" -> "kaiojorge": nome do arquivo em public/jogadores (sem acento, espaço ou pontuação). */
export function arquivoRosto(nome: string): string {
  return nome.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z0-9]/g, '');
}

/**
 * Foto do jogador sobre a camisa. Procura primeiro `<nome>-<sigla do clube>.png` (ex.: joaocosta-cru.png), depois
 * `<nome>-<posição>.png` (ex.: otavio-g.png) e por fim `<nome>.png`. Se não houver arquivo, a camisa continua aparecendo.
 */
export function rosto(nome: string, posicao = '', sigla = ''): string {
  const base = arquivoRosto(nome);
  if (!base) return '';
  const doClube = sigla ? `${base}-${sigla.toLowerCase()}` : '';
  const dePosicao = posicao ? `${base}-${posicao.toLowerCase()}` : '';
  const candidatos = [doClube, dePosicao, base].filter((a) => a && !semRosto.has(a));
  const primeiro = candidatos[0];
  if (!primeiro) return '';
  return `<img class="rosto-img" data-rosto="${esc(primeiro)}" data-resto="${esc(candidatos.slice(1).join(','))}" src="./jogadores/${primeiro}.png" alt="" decoding="sync" draggable="false" />`;
}

/** Camisa listrada do clube (ou preta, para o goleiro). Desenhada do zero. */
export function camisa(c1: string, c2: string, listrada = true, rotulo = ''): string {
  const a = cor(c1);
  const b = cor(c2);
  const listras = listrada
    ? `<rect x="11" y="9" width="3" height="21" fill="${b}"/><rect x="17" y="9" width="3" height="21" fill="${b}"/><rect x="23" y="9" width="3" height="21" fill="${b}"/>`
    : '';
  return `<svg class="camisa" width="34" height="34" viewBox="0 0 38 34" aria-hidden="true">
    <path d="M12 2 3 7l3 7 4-2v20h18V12l4 2 3-7-9-5a7 7 0 0 1-14 0z" fill="${a}" stroke="#111" stroke-opacity=".55" stroke-width="1"/>
    ${listras}${rotulo ? `<text x="19" y="23" text-anchor="middle" font-size="10" font-weight="700" fill="#fff" stroke="#000" stroke-width=".4">${esc(rotulo)}</text>` : ''}</svg>`;
}

/** Camisa cinza de reserva vazia (como os slots do banco no original). */
export function camisaVazia(): string {
  return `<svg class="camisa vazia" width="34" height="34" viewBox="0 0 38 34" aria-hidden="true">
    <path d="M12 2 3 7l3 7 4-2v20h18V12l4 2 3-7-9-5a7 7 0 0 1-14 0z" fill="#e9e9e9" stroke="#999" stroke-width="1"/></svg>`;
}
